/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * MyPaymentsPage — /feed/payments
 * ---------------------------------------
 * Every payment the signed-in person has made, newest first, ten to a page,
 * as a statement. Successful, still settling, abandoned and refunded alike:
 * somebody querying a charge needs to find the one that did NOT work, so a
 * failed payment is as much the point of this page as a paid one.
 *
 * Scoped server-side to their own ApplicationUser — see MyPaymentsAPIView.
 * Nothing here says whose payments to fetch, and nothing here could ask
 * for anybody else's.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, LoadError, Skeleton } from '../../../components/feed/FeedParts';
import PublicEmptyState from '../../../components/public/PublicEmptyState';
import envConfig from '../../../config/envConfig';
import { PAGE_SIZE, STATUS_FILTERS } from '../../../components/athlohive/myPayments';
import useMyPayments from './useMyPayments';
// import useMyPayments, { usePaymentNames } from './useMyPayments';
import PaymentCard from './PaymentCard';
import PaymentSupportModal from './PaymentSupportModal';
import { EVENTS_PATH } from '../feedRoutes';
import useShellNav from '../useShellNav';

/* The feed's own warm ink, handed to the shared empty-state block so it does
   not read as the portal's cooler slate on this page. */
const FEED_EMPTY_INK = '#241708';
const FEED_EMPTY_SUB = 'rgba(36, 23, 8, 0.60)';

function Loading() {
  return (
    <div className="fd-pay-list" aria-busy="true">
      {[0, 1, 2, 3, 4].map((n) => (
        <div className="fd-pay" key={n}>
          <div className="fd-pay-hd">
            <Skeleton height={40} radius={11} style={{ width: 40, flexShrink: 0 }} />
            <div className="fd-pay-mid">
              <Skeleton height={15} radius={6} style={{ width: '60%' }} />
              <Skeleton height={12} radius={6} style={{ width: '38%', marginTop: 7 }} />
            </div>
            <Skeleton height={18} radius={6} style={{ width: 72, flexShrink: 0 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyPaymentsPage() {
  const navigate = useNavigate();
  const embedded = useShellNav();
  const [filterKey, setFilterKey] = useState('all');
  const [page, setPage] = useState(1);
  // A SET, not one id: anything that did not succeed is opened on arrival, so
  // several rows are open at once and each can still be toggled by hand.
  const [openOrders, setOpenOrders] = useState(() => new Set());

  const filter = useMemo(
    () => STATUS_FILTERS.find((f) => f.key === filterKey) || STATUS_FILTERS[0],
    [filterKey],
  );

  const { data, isLoading, isError, refetch } = useMyPayments({
    page, statuses: filter.statuses,
  });

  const rows = data?.rows || [];

  /*
   * THE ACTIVITY NAME IS NO LONGER FETCHED, and that is the whole reason this
   * screen now costs ONE request.
   *
   * The event's title rides along with the payments themselves (the server
   * resolves it from label_table/label_column — see fetchMyPayments), because
   * a payment HOLDS its event id and `event` is a public table: one id, one
   * hop, readable.
   *
   * The activity is two hops through a PRIVATE table —
   * instance_id -> event_registration -> event_activity — and the lookup is
   * deliberately gated to public tables only. So naming an activity meant
   * reading each event separately: one request per event on the page, plus a
   * registration read to learn which activity each payment was for. Six
   * requests to add a few words to rows that already say what was paid, when,
   * how much, and whether it worked.
   *
   * Kept rather than deleted: if event_activity is ever granted to
   * participants, restoring this is uncommenting two lines.
   */
  // const { naming, regActivities } = usePaymentNames(rows);
  const naming = null;
  const regActivities = null;

  // PAGE_SIZE, never a literal: the request and this label must agree, or the
  // pager announces pages that do not exist the moment the size changes.
  const pageCount = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE));

  /*
   * OPEN WHAT WENT WRONG, BEFORE ANYONE ASKS.
   *
   * Somebody opening this screen is usually here because a payment did not
   * work, and the sentence explaining it — "nothing was charged", "your bank
   * is still confirming" — lives inside the expanded row. Making them tap each
   * one to find that out is making them hunt for the answer they came for.
   *
   * So every payment that is not `paid` starts open, and the successful ones
   * stay collapsed: a successful payment has nothing to explain.
   *
   * Recomputed only when the ROWS THEMSELVES change (a new page, a different
   * filter), never on an ordinary re-render — otherwise a row the reader just
   * collapsed would spring open again under their hand.
   */
  const rowsKey = rows.map((row) => row.orderId).join(',');
  const lastKey = useRef(null);
  useEffect(() => {
    if (lastKey.current === rowsKey) return;
    lastKey.current = rowsKey;
    setOpenOrders(new Set(
      rows.filter((row) => row.status !== 'paid').map((row) => row.orderId),
    ));
  }, [rowsKey, rows]);

  const toggle = (orderId) => setOpenOrders((open) => {
    const next = new Set(open);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    return next;
  });
  // const supportEmail = envConfig.supportEmail;
  /*
   * WRITING TO US NO LONGER NEEDS A MAILBOX IN THIS APP.
   *
   * It used to be a mailto: built from REACT_APP_SUPPORT_EMAIL, which is only
   * as good as the reader's machine: a browser with no mail client wired up
   * answers that link with nothing at all, and the person reading this page is
   * usually here BECAUSE a payment failed. So the support channel is now the
   * contact form itself, opened in a modal with the payment already written
   * into it.
   *
   * What is configured is the PAGE, not an address — the backend already knows
   * who to notify for that form. No slug, no channel; the invitation is simply
   * not offered, exactly as an unset mailbox used to hide it.
   */
  const supportOffered = !!envConfig.contactPageSlug;

  // Which payment the support modal is about. `null` is closed; a row is that
  // payment; `'general'` is the footer's own invitation, which names no order.
  const [supportFor, setSupportFor] = useState(null);

  const choose = (key) => {
    setFilterKey(key);
    // A filter changes which rows exist, so the page number it was on is
    // meaningless against the new list.
    setPage(1);
  };

  const body = () => {
    if (isLoading && !data) return <Loading />;

    if (isError) {
      return <LoadError what="your payments" onRetry={refetch} />;
    }

    if (!rows.length) {
      // "You have none" and "this filter found none" are different facts and
      // must never look the same.
      return filter.statuses ? (
        <PublicEmptyState
          variant="no-results"
          icon="bi-funnel"
          title={`No ${filter.label.toLowerCase()} payments`}
          subtitle="Nothing matched this filter. Clear it to see every payment on your account."
          titleColor={FEED_EMPTY_INK}
          subtitleColor={FEED_EMPTY_SUB}
          clearLabel="Show all payments"
          onClear={() => choose('all')}
        />
      ) : (
        <PublicEmptyState
          icon="bi-receipt"
          title="No payments yet"
          subtitle="Your payments appear here the moment you register for an event. Nothing has been charged to you so far — book a place and the receipt shows up here right away."
          titleColor={FEED_EMPTY_INK}
          subtitleColor={FEED_EMPTY_SUB}
          action={{
            label: 'Find an event',
            icon: 'bi-calendar4-week',
            onClick: () => navigate(embedded ? '/feed/events' : EVENTS_PATH),
          }}
        />
      );
    }

    return (
      <>
        <div className="fd-pay-list">
          {rows.map((row) => (
            <PaymentCard
              key={row.orderId}
              row={row}
              naming={naming}
              regActivities={regActivities}
              expanded={openOrders.has(row.orderId)}
              onToggle={() => toggle(row.orderId)}
              onSupport={supportOffered ? setSupportFor : undefined}
            />
          ))}
        </div>

        {pageCount > 1 && (
          <div className="fd-pager">
            <button
              type="button"
              onClick={() => setPage((n) => Math.max(1, n - 1))}
              disabled={!data?.hasPrevious}
            >
              <Icon name="chevron_left" />Prev
            </button>
            <span className="fd-pager-at">PAGE {page} OF {pageCount}</span>
            <button
              type="button"
              onClick={() => setPage((n) => n + 1)}
              disabled={!data?.hasNext}
            >
              Next<Icon name="chevron_right" />
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <main className="fd-main fd-pay-page">
      <header className="fd-pay-head">
        <p className="fd-eyebrow">Payments</p>
        <h1>My payments</h1>
        <p className="fd-pay-lede">
          Every payment made from this account, newest first.
        </p>
      </header>

      <div className="fd-pay-chips" role="tablist" aria-label="Filter by status">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={f.key === filterKey}
            className={f.key === filterKey ? 'on' : ''}
            onClick={() => choose(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {body()}

      {/* The same invitation, when it still named a mailbox:
      {supportEmail && (
        <aside className="fd-pay-help">
          <b>Questions about a payment?</b>
          <p>
            If any payment above looks incorrect, or an amount was deducted
            without your registration being confirmed, write to us at{' '}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with the order
            ID shown on the payment. We will look into it and get back to
            you within 2 business days.
          </p>
        </aside>
      )} */}
      {supportOffered && (
        <aside className="fd-pay-help">
          <b>Questions about a payment?</b>
          <p>
            If any payment above looks incorrect, or an amount was deducted
            without your registration being confirmed, write to us with the
            order ID shown on the payment. We will look into it and get back
            to you within 2 business days.
          </p>
          <button
            type="button"
            className="fd-pay-help-btn"
            onClick={() => setSupportFor('general')}
          >
            <Icon name="mail" />Write to us about a payment
          </button>
        </aside>
      )}

      {supportFor && (
        <PaymentSupportModal
          row={supportFor === 'general' ? null : supportFor}
          onClose={() => setSupportFor(null)}
        />
      )}
    </main>
  );
}
